import { useOrganization } from "@/modules/shared/hooks/useOrganization";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { differenceInDays } from "date-fns";

interface TrialInfo {
  isTrialing: boolean;
  isExpired: boolean;
  isActive: boolean;
  isPendingApproval: boolean;
  isSuspended: boolean;
  isBlocked: boolean;
  daysRemaining: number;
  trialEndsAt: Date | null;
  subscriptionStatus: string | null;
}

export function useTrialStatus(): TrialInfo & { loading: boolean } {
  const { user } = useAuth();
  const { organizationId, loading: orgLoading } = useOrganization();

  const { data, isPending } = useQuery({
    queryKey: ["trial-status", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await db
        .from("organizations")
        .select("subscription_status, trial_starts_at, trial_ends_at")
        .eq("id", organizationId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!organizationId && !!user,
    staleTime: 60_000,
  });

  const now = new Date();
  const trialEndsAt = data?.trial_ends_at ? new Date(data.trial_ends_at) : null;
  const status = data?.subscription_status ?? null;

  const isTrialing = status === "trialing";
  const isExpired = isTrialing && !!trialEndsAt && trialEndsAt < now;
  const isActive = status === "active";
  const isPendingApproval = status === "pending_approval";
  const isSuspended = status === "suspended";
  const isBlocked = status === "blocked";
  const daysRemaining = trialEndsAt ? Math.max(0, differenceInDays(trialEndsAt, now)) : 0;

  return {
    isTrialing,
    isExpired,
    isActive,
    isPendingApproval,
    isSuspended,
    isBlocked,
    daysRemaining,
    trialEndsAt,
    subscriptionStatus: status,
    loading: isPending || orgLoading,
  };
}
